import React, { useState, useEffect } from 'react'

import { useSnackbar } from 'contexts'
import { useCreateShopifyWidgetPage, useShopifyConnectionStatus } from 'hooks/api/useShopifyIntegration'
import { useConnectShopify } from 'hooks/api'
import { normalizeShopifyDomain } from 'utils'

import { Stack, Typography, Box } from '@mui/material'
import { ChevronLeft } from 'lucide-react'

import { Modal, Button, Alert, Input, Stepper } from '@repo/ui'

interface InstallationHandleModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (result: any) => void
}

// (Removed MODAL_NOTE_SX.) The in-modal notes used the `neutral` toast look plus an
// sx that forced transparent + tighter padding (10/8px against the shared 12/16px).
// That is why the info note and the success row — sitting in the same modal, one
// above the other — had visibly different padding and only one had a fill. They are
// now plain banners: identical padding from StyledMuiAlert, and `info` picks up its
// own faint grey tint (TINT_HUE.info) the same way success picks up green.

// VRG-724 — `normalizeShopifyDomain` moved to utils/shopifyDomain (shared with ConnectShopifyModal).

export const InstallationHandleModal = (props: InstallationHandleModalProps) => {
  const { open, onClose, onSuccess } = props
  const [handle, setHandle] = useState('certificates')
  const [activeStep, setActiveStep] = useState(0)
  const [shopDomain, setShopDomain] = useState('')

  const { open: notification } = useSnackbar()
  const createWidgetPageMutation = useCreateShopifyWidgetPage()
  const { data: shopifyStatus, refetch: refetchShopifyStatus } = useShopifyConnectionStatus()
  const { connectShopify, isConnecting } = useConnectShopify()

  const steps = ['Connect Shopify', 'Install Vera']

  // Check connection status when modal opens
  useEffect(() => {
    if (open) {
      refetchShopifyStatus()
      // If already connected, skip to step 2
      if (shopifyStatus?.connected) {
        setActiveStep(1)
      } else {
        setActiveStep(0)
      }
    }
  }, [open, shopifyStatus?.connected, refetchShopifyStatus])

  const handleConnectShopify = async () => {
    if (!shopDomain.trim()) {
      notification({
        type: 'error',
        title: 'Please enter your Shopify store domain',
      })
      return
    }

    const normalizedDomain = normalizeShopifyDomain(shopDomain)
    if (!normalizedDomain) {
      notification({
        type: 'error',
        title: 'Enter your Shopify store URL (e.g. your-store.myshopify.com or admin.shopify.com/store/your-store)',
      })
      return
    }

    try {
      const response = await connectShopify({
        shop_domain: normalizedDomain,
        origin: 'stepper'
      })

      notification({
        type: 'success',
        title: 'Redirecting to Shopify for authorization...',
      })

      // Redirect to Shopify OAuth URL
      window.location.href = response.auth_url
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to Shopify'
      notification({
        type: 'error',
        title: 'Connection Failed',
        text: errorMessage,
      })
    }
  }

  const handleNextStep = () => {
    if (activeStep === 0) {
      // Check if connected before moving to next step
      if (shopifyStatus?.connected) {
        setActiveStep(1)
      } else {
        notification({
          type: 'error',
          title: 'Please connect to Shopify first',
        })
      }
    }
  }

  const handleInstall = async () => {
    if (!handle.trim()) {
      notification({
        type: 'error',
        title: 'Please enter a page handle',
      })
      return
    }

    // Validate handle format (URL slug format)
    const handleRegex = /^[a-z0-9-]+$/
    if (!handleRegex.test(handle.trim())) {
      notification({
        type: 'error',
        title: 'Handle must contain only lowercase letters, numbers, and hyphens',
      })
      return
    }

    try {
      const result = await createWidgetPageMutation.mutateAsync({
        title: 'Certificates',
        handle: handle.trim(),
        isPublished: true,
      })

      notification({
        type: 'success',
        title: 'Widget Page Created!',
        text: `Successfully created page "${result.title}" in your Shopify store.`,
      })

      // Optionally open the created page in a new tab
      if (result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer')
      }

      if (onSuccess) {
        onSuccess(result)
      }

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create widget page'

      // Check if the error is about handle/page already being taken
      if (errorMessage.toLowerCase().includes('handle') || errorMessage.toLowerCase().includes('already exists') || errorMessage.toLowerCase().includes('taken')) {
        notification({
          type: 'error',
          title: `Failed to install because page "${handle}" already exists on Shopify. Please use a different name.`,
        })
      } else {
        notification({
          type: 'error',
          title: 'Installation Failed',
          text: errorMessage,
        })
      }
    }
  }

  const handleClose = () => {
    setHandle('certificates')
    setActiveStep(0)
    setShopDomain('')
    onClose()
  }

  const isValidHandle = (handleValue: string) => {
    const handleRegex = /^[a-z0-9-]+$/
    return handleRegex.test(handleValue.trim())
  }

  const normalizedHandle = handle.toLowerCase().replace(/[^a-z0-9-]/g, '-')

  const isValidDomain = (domain: string) => normalizeShopifyDomain(domain) !== null

  return (
    <Modal
      title="Shopify install"
      open={open}
      onClose={handleClose}
      actions={
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          {/* Back lives bottom-left so it reads as "go to the previous step", separate
              from the Cancel/confirm group on the right. Only shown past step 1. */}
          <Box>
            {activeStep === 1 && (
              <Button
                disableElevation
                variant="tertiary"
                startIcon={<ChevronLeft size={16} />}
                onClick={() => setActiveStep(0)}
                disabled={createWidgetPageMutation.isPending}
              >
                Back
              </Button>
            )}
          </Box>

          <Stack direction="row" alignItems="center" spacing={3}>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleClose}
              disabled={createWidgetPageMutation.isPending || isConnecting}
            >
              Cancel
            </Button>

            {activeStep === 0 && !shopifyStatus?.connected && (
              <Button
                variant="primary"
                disableElevation
                onClick={handleConnectShopify}
                loading={isConnecting}
                disabled={!shopDomain || !shopDomain.trim() || !isValidDomain(shopDomain)}
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            )}

            {activeStep === 0 && shopifyStatus?.connected && (
              <Button
                variant="primary"
                disableElevation
                onClick={handleNextStep}
              >
                Next
              </Button>
            )}

            {activeStep === 1 && (
              <Button
                variant="primary"
                disableElevation
                onClick={handleInstall}
                loading={createWidgetPageMutation.isPending}
                disabled={!handle.trim() || !isValidHandle(handle)}
              >
                {createWidgetPageMutation.isPending ? 'Installing...' : 'Install'}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      <Stack spacing={4} pt={2} pb={4}>
        <Stepper steps={steps} activeStep={activeStep} variant="bar" />

        {activeStep === 0 && (
          <Stack spacing={3} pt={0}>
            {shopifyStatus?.connected && (
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>
                Your Shopify integration is connected. Click Next to proceed with page creation.
              </Typography>
            )}

            {!shopifyStatus?.connected && (
              <>
                <Stack spacing={1} pt={6}>
                  {/* Label row: field name left, help link right (the 'Forgot password?'
                      pattern). TODO(docs): point the link at the "find your myshopify
                      domain" help article once the docs site is built. */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" pb={1}>
                    <Typography variant="body3">MyShopify domain</Typography>
                    <Typography
                      variant="body3"
                      onClick={() => {
                        // TODO(docs): window.open('<help-article-url>', '_blank')
                      }}
                      sx={{
                        fontSize: 12,
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      Where do I find this?
                    </Typography>
                  </Stack>
                  <Input
                    placeholder="your-store.myshopify.com"
                    value={shopDomain}
                    onChange={(value) => {
                      // Remove https:// or http:// if present
                      const cleanedValue = value.replace(/^https?:\/\//, '')
                      setShopDomain(cleanedValue)
                    }}
                    error={shopDomain && !isValidDomain(shopDomain)}
                    helperText={shopDomain && !isValidDomain(shopDomain) ? "Enter your store URL (your-store.myshopify.com or admin.shopify.com/store/your-store)" : ""}
                  />
                </Stack>

                <Alert
                  severity="info"
                  description="You'll be redirected to Shopify to authorize the connection. Make sure you have admin access to your store."
                />
              </>
            )}

            {/* Reports an accomplished state — "your store is already connected,
                that step is done" — which is the one thing on this step worth
                colouring. */}
            {shopifyStatus?.connected && (
              <Alert
                severity="success"
                description={`Connected to ${shopifyStatus.shop_domain || 'Shopify store'}`}
              />
            )}
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={3} pt={0}>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
              Choose a page name for your certificates page. This will be part of the URL where customers can view their certificates.
            </Typography>

            <Stack spacing={1} pt={3}>
              <Typography variant="body3" sx={{ pb: 1 }}>Page Name</Typography>
              <Input
                placeholder="certificates"
                value={handle}
                onChange={(value) => setHandle(value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                error={handle && !isValidHandle(handle)}
                helperText={
                  handle && !isValidHandle(handle)
                    ? "Page name must contain only lowercase letters, numbers, and hyphens"
                    : `Page URL: ${shopifyStatus?.shop_domain || 'your-store.myshopify.com'}/pages/${handle || 'certificates'}`
                }
              />
            </Stack>

            <Alert
              severity="info"
              description="This will create a new page in your Shopify store and install Vera. You'll still need to add that page to your store's navigation menu."
            />
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}